import { type ReactNode, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { Container, Me } from "@workcore/schemas";
import { api, checkedWrite, get } from "../../api";
import { useDefinitions, useRefresh, wsKey } from "../../directory";
import { EmptyState, ErrorBox, Field, PageHeader, Panel, RowMenu, SidePanel, StatusBadge, When, when } from "../../ui";
import { toKey } from "../editor/shared";
import { HistoryLink } from "../../activity";
import { confirmAction } from "../../confirm";

export function useMe(slug: string) {
  return useQuery({ queryKey: ["ws-me", slug], queryFn: async () => Me.parse(await get(`/v1/ws/${slug}/me`)) });
}

function NewProject({ slug, containers, open, onDone }: { slug: string; containers: Container[]; open: boolean; onDone: () => void }) {
  const defs = (useDefinitions(slug).data ?? []).filter((d) => d.status === "active");
  const refresh = useRefresh(slug);
  const templates = containers.filter((c) => c.isTemplate && c.status === "active");
  const [name, setName] = useState("");
  const [source, setSource] = useState("");
  const [start, setStart] = useState("");
  const fromTemplate = source.startsWith("t:");
  const create = useMutation({
    mutationFn: () =>
      checkedWrite("POST", `/v1/ws/${slug}/containers`, {
        key: toKey(name).replace(/_/g, "-"),
        name,
        ...(fromTemplate ? { fromTemplateId: Number(source.slice(2)), ...(start ? { startDate: start } : {}) } : { definitionId: Number(source.slice(2)) }),
      }),
    onSuccess: () => (refresh(), setName(""), setSource(""), setStart(""), onDone()),
  });
  return (
    <SidePanel title="New project" open={open} onClose={onDone} onSubmit={() => create.mutate()} submitLabel="Create project" busy={create.isPending} error={create.error}>
      <Field label="Project name" help={`Key: ${toKey(name).replace(/_/g, "-") || "…"} (it never changes)`}>
        <input value={name} required autoFocus onChange={(e) => setName(e.target.value)} placeholder="NORA Phase 2" />
      </Field>
      <Field label="Start from" help="A template copies its structure; a workflow starts the project empty.">
        <select required value={source} onChange={(e) => setSource(e.target.value)}>
          <option value="">Choose…</option>
          <optgroup label="Template">
            {templates.map((t) => <option key={t.id} value={`t:${t.id}`}>{t.name}</option>)}
          </optgroup>
          <optgroup label="Empty, on a workflow">
            {defs.map((d) => <option key={d.id} value={`d:${d.id}`}>{d.name}</option>)}
          </optgroup>
        </select>
      </Field>
      {fromTemplate && (
        <Field label="Start date" help="Planned dates shift so the template's earliest date lands here. Leave empty to clear them.">
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </Field>
      )}
    </SidePanel>
  );
}

export function ProjectsPage() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const me = useMe(slug).data;
  const defs = useDefinitions(slug).data ?? [];
  const refresh = useRefresh(slug);
  const list = useQuery({ queryKey: wsKey(slug, "containers-all"), queryFn: async () => z.array(Container).parse(await get(`/v1/ws/${slug}/containers`, { include_archived: "true" })) });
  const [adding, setAdding] = useState(false);
  const act = useMutation({
    mutationFn: ({ c, body, path }: { c: Container; body: unknown; path?: string }) => checkedWrite(path ? "POST" : "PATCH", `/v1/ws/${slug}/containers/${c.id}${path ?? ""}`, body),
    onSuccess: refresh,
  });
  const isSys = me?.isSystemAdmin ?? false;
  const projectAdminOf = new Set((me?.roles ?? []).filter((r) => r.role === "project_admin").map((r) => r.scopeId));
  const workflowAdminOf = new Set((me?.roles ?? []).filter((r) => r.role === "workflow_admin").map((r) => r.scopeId));
  const canCreate = isSys || workflowAdminOf.size > 0;
  const rows = list.data ?? [];
  const section = (title: string | undefined, items: Container[], empty: ReactNode) => (
    <Panel {...(title ? { title } : {})} plain>
      {items.length === 0 ? (
        empty
      ) : (
        <table className="grid">
          <thead><tr><th>Name</th><th>Workflow</th><th>Status</th><th className="opt">Created</th><th>You</th><th /></tr></thead>
          <tbody>
            {items.map((c) => {
              const pa = projectAdminOf.has(c.id);
              const wa = workflowAdminOf.has(c.definitionId);
              return (
                <tr key={c.id} className={c.status === "archived" ? "inactive" : ""}>
                  <td>
                    {pa || isSys ? <Link to={`/w/${slug}/projects/${c.id}/${c.isTemplate ? "structure" : "access"}`}>{c.name}</Link> : c.name} <code className="small">{c.key}</code>
                  </td>
                  <td>{defs.find((d) => d.id === c.definitionId)?.name ?? `#${c.definitionId}`}</td>
                  <td><StatusBadge status={c.status} /></td>
                  <td className="opt"><When at={c.createdAt} /></td>
                  <td className="muted small">{[pa && "project admin", wa && "workflow admin", isSys && "system admin"].filter(Boolean).join(", ") || "—"}</td>
                  <td className="row-actions">
                    <RowMenu
                      label={`Actions for ${c.name}`}
                      items={[
                        (pa || isSys) && { label: "Access", onSelect: () => navigate(`/w/${slug}/projects/${c.id}/access`) },
                        c.isTemplate && { label: "Structure", onSelect: () => navigate(`/w/${slug}/projects/${c.id}/structure`) },
                        (pa || isSys || (c.isTemplate && wa)) && { label: "Periods", onSelect: () => navigate(`/w/${slug}/projects/${c.id}/periods`) },
                        (isSys || wa) && { label: c.isTemplate ? "Not a template" : "Make template", onSelect: () => act.mutate({ c, path: "/set-template", body: { isTemplate: !c.isTemplate } }) },
                        (isSys || pa) && c.status === "archived" && { label: "Unarchive", onSelect: () => act.mutate({ c, body: { status: "active" } }) },
                        (isSys || pa) && c.status === "active" && {
                          label: "Archive…",
                          danger: true,
                          onSelect: () =>
                            void confirmAction({ title: `Archive project '${c.name}'?`, body: "It becomes read-only. Nothing is deleted, and you can unarchive it.", action: "Archive", check: () => api("PATCH", `/v1/ws/${slug}/containers/${c.id}`, { body: { status: "archived" }, dryRun: true }) }).then((ok) => ok && act.mutate({ c, body: { status: "archived" } })),
                        },
                      ]}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Panel>
  );
  return (
    <>
      <PageHeader title="Projects" description="Projects run on a workflow. Templates hold the standard structure new projects start from; the workflow's admins edit them." actions={<><HistoryLink slug={slug} kind="projects" />{canCreate && <button onClick={() => setAdding(true)}>New project</button>}</>} />
      {canCreate && <NewProject slug={slug} containers={rows} open={adding} onDone={() => setAdding(false)} />}
      <ErrorBox error={list.error ?? act.error} />
      {list.isSuccess &&
        section(
          undefined,
          rows.filter((c) => !c.isTemplate),
          <EmptyState title="No projects yet" body="A project runs on a workflow. Start one empty, or from a template so it begins with a standard structure." action={canCreate && <button onClick={() => setAdding(true)}>New project</button>} />,
        )}
      {list.isSuccess &&
        section(
          "Templates",
          rows.filter((c) => c.isTemplate),
          <EmptyState title="No templates yet" body="Make any project a template from its ⋯ menu. New projects can then start from a copy of its structure, with dates moved to their own start." />,
        )}
    </>
  );
}

